import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

// ── Multi-select dropdown ─────────────────────────────────────────────────────
function MultiSelect({ label, options, value, onChange, placeholder = 'All', disabled = false }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const ref     = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setSearch('');
  }, [open]);

  const allSelected = value.length === 0;
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const label_text = allSelected
    ? placeholder
    : value.length === 1
    ? (options.find((o) => o.value === value[0])?.label ?? value[0])
    : `${value.length} selected`;

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div ref={ref} className="relative">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition
          ${open ? 'border-orange-400 ring-2 ring-orange-200' : 'border-slate-300 hover:border-slate-400'}
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <span className={allSelected ? 'text-slate-400' : ''}>{label_text}</span>
        <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg" style={{ minWidth: '200px' }}>
          {/* Search input */}
          <div className="border-b border-slate-100 px-2 py-1.5">
            <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
              <svg className="h-3.5 w-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to filter…"
                className="w-full bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="shrink-0 text-slate-400 hover:text-slate-600">
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {/* All option — only when not searching */}
          {!search && (
            <button
              type="button"
              onClick={() => { onChange([]); setOpen(false); setSearch(''); }}
              className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${allSelected ? 'font-semibold text-orange-600' : 'text-slate-600'}`}
            >
              All
            </button>
          )}
          <div className="max-h-56 overflow-y-auto py-0.5">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">No matches</p>
            ) : (
              filtered.map((opt) => (
                <label key={opt.value} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={value.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="h-3.5 w-3.5 rounded border-slate-300 accent-orange-600"
                  />
                  <span className="flex-1 text-sm text-slate-700">{opt.label}</span>
                  {opt.scheduleType && (
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize leading-none
                      ${opt.scheduleType === 'weekend' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                      {opt.scheduleType}
                    </span>
                  )}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
function Badge({ children, color = 'gray' }) {
  const cls = {
    red:    'bg-red-100 text-red-700',
    amber:  'bg-amber-100 text-amber-700',
    green:  'bg-green-100 text-green-700',
    blue:   'bg-blue-100 text-blue-700',
    gray:   'bg-slate-100 text-slate-600',
    orange: 'bg-orange-100 text-orange-700',
  }[color] ?? 'bg-slate-100 text-slate-600';
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ${cls}`}>{children}</span>;
}

// ── Batch status colours ──────────────────────────────────────────────────────
const STATUS_COLORS = {
  'not started':        'gray',
  'ongoing':            'green',
  'delivery completed': 'blue',
  'batch completed':    'orange',
};

const BATCH_STATUS_OPTIONS = [
  { value: 'not started',        label: 'Not Started' },
  { value: 'ongoing',            label: 'Ongoing' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed',    label: 'Batch Completed' },
];

// ── Student row in subject table ──────────────────────────────────────────────
function StudentRow({ student, type }) {
  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="py-2 pl-4 pr-2 text-xs text-slate-500">{student.registrationNumber || '—'}</td>
      <td className="py-2 pr-4 text-xs font-medium text-slate-800">{student.studentName || '—'}</td>
      <td className="py-2 pr-4">
        {type === 'repeat' ? (
          <Badge color="red">R – Repeat</Badge>
        ) : (
          <Badge color="amber">Not Submitted</Badge>
        )}
      </td>
    </tr>
  );
}

function daysDiff(fromDateStr, toDateStr) {
  if (!fromDateStr || !toDateStr) return null;
  const from = new Date(fromDateStr);
  const to   = new Date(toDateStr);
  return Math.round((to - from) / 86400000);
}

// ── Subject block ─────────────────────────────────────────────────────────────
function SubjectBlock({ subj }) {
  const [expanded, setExpanded] = useState(true);
  const total = subj.repeat.length + subj.notSubmitted.length;

  const planToActual = daysDiff(subj.plannedSubmissionDate, subj.actualSubmissionDate);
  const today = new Date().toISOString().slice(0, 10);
  const daysOverdue = daysDiff(subj.actualSubmissionDate, today);

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02z" clipRule="evenodd" />
          </svg>
          <span className="font-semibold text-slate-800">{subj.subjectName}</span>
          {subj.subjectShortName && subj.subjectShortName !== subj.subjectName && (
            <span className="text-xs text-slate-400">({subj.subjectShortName})</span>
          )}
          {subj.semester && <span className="text-xs text-slate-400">· Sem {subj.semester}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {subj.repeat.length > 0 && <Badge color="red">{subj.repeat.length} Repeat</Badge>}
          {subj.notSubmitted.length > 0 && <Badge color="amber">{subj.notSubmitted.length} Not Submitted</Badge>}
          <Badge color="gray">{total} total</Badge>
        </div>
      </button>

      {/* Subject meta row */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-100 bg-slate-50 px-4 py-2">
        {subj.lecturerName && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            <svg className="h-3 w-3 shrink-0 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-7 9a7 7 0 1 1 14 0H3z" clipRule="evenodd" />
            </svg>
            <span className="font-medium text-slate-700">{subj.lecturerName}</span>
          </span>
        )}
        {(subj.expectedHours != null) && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            <svg className="h-3 w-3 shrink-0 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1-12a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l2.828 2.829a1 1 0 1 0 1.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Expected: <span className="font-medium text-slate-700">{subj.expectedHours}h</span>
          </span>
        )}
        {(subj.scheduledHours != null) && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            <svg className="h-3 w-3 shrink-0 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6 2a1 1 0 0 0-1 1v1H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1V3a1 1 0 1 0-2 0v1H7V3a1 1 0 0 0-1-1zM4 8h12v8H4V8z" />
            </svg>
            Scheduled: <span className="font-medium text-slate-700">{subj.scheduledHours}h</span>
          </span>
        )}
        {subj.plannedSubmissionDate && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            Planned deadline: <span className="font-medium text-slate-700">{subj.plannedSubmissionDate}</span>
          </span>
        )}
        {subj.actualSubmissionDate && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            Actual deadline: <span className="font-medium text-red-600">{subj.actualSubmissionDate}</span>
          </span>
        )}
        {planToActual !== null && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            Variation:&nbsp;
            <span className={`font-semibold ${planToActual > 0 ? 'text-orange-600' : planToActual < 0 ? 'text-green-600' : 'text-slate-600'}`}>
              {planToActual === 0 ? 'on plan' : planToActual > 0 ? `+${planToActual}d late` : `${Math.abs(planToActual)}d early`}
            </span>
          </span>
        )}
        {daysOverdue !== null && daysOverdue > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            Overdue:&nbsp;
            <span className="font-semibold text-red-600">{daysOverdue}d ago</span>
          </span>
        )}
      </div>
      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3">
          {subj.repeat.length > 0 && (
            <div className="mb-4">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-500">Repeat ({subj.repeat.length})</p>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="pb-1 pl-4 text-[10px] font-semibold uppercase text-slate-400">Reg No.</th>
                    <th className="pb-1 text-[10px] font-semibold uppercase text-slate-400">Name</th>
                    <th className="pb-1 text-[10px] font-semibold uppercase text-slate-400">Mark</th>
                  </tr>
                </thead>
                <tbody>
                  {subj.repeat.map((s) => <StudentRow key={s.studentId} student={s} type="repeat" />)}
                </tbody>
              </table>
            </div>
          )}
          {subj.notSubmitted.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-500">Not Submitted ({subj.notSubmitted.length})</p>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="pb-1 pl-4 text-[10px] font-semibold uppercase text-slate-400">Reg No.</th>
                    <th className="pb-1 text-[10px] font-semibold uppercase text-slate-400">Name</th>
                    <th className="pb-1 text-[10px] font-semibold uppercase text-slate-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subj.notSubmitted.map((s) => <StudentRow key={s.studentId} student={s} type="not-submitted" />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Batch block ───────────────────────────────────────────────────────────────
function BatchBlock({ batch }) {
  const [expanded, setExpanded] = useState(true);
  const statusColor = STATUS_COLORS[batch.status] ?? 'gray';

  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between rounded-xl px-5 py-3.5 text-left"
      >
        <div className="flex items-center gap-3">
          <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02z" clipRule="evenodd" />
          </svg>
          <div>
            <span className="font-semibold text-slate-800">{batch.batchInternalName || batch.batchName}</span>
            {batch.whatsappGroupName && (
              <span className="ml-2 text-xs text-slate-400">({batch.whatsappGroupName})</span>
            )}
          </div>
          <Badge color={statusColor}>{batch.status}</Badge>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {batch.totalRepeat > 0 && <Badge color="red">{batch.totalRepeat} Repeat</Badge>}
          {batch.totalNotSubmitted > 0 && <Badge color="amber">{batch.totalNotSubmitted} Not Submitted</Badge>}
          <Badge color="gray">{batch.subjects.length} subject{batch.subjects.length !== 1 ? 's' : ''}</Badge>
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-5">
          {batch.subjects.map((subj) => (
            <SubjectBlock key={subj.batchSubjectId} subj={subj} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HndSubmissionAnalysis() {
  const [filterOptions, setFilterOptions] = useState({ courses: [], batches: [] });
  const [selectedCourses, setSelectedCourses]   = useState([]);
  const [selectedBatches, setSelectedBatches]   = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [applied, setApplied] = useState(false);

  // Load filter options on mount
  useEffect(() => {
    axiosInstance.get('/admin/dashboard/hnd-submission-analysis', {
      params: { courseIds: 'all', batchIds: 'all', batchStatuses: 'all' },
    }).then((res) => {
      if (res?.data?.filterOptions) {
        setFilterOptions(res.data.filterOptions);
      }
    }).catch(() => {});
  }, []);

  // Batches visible in the batch dropdown — filtered by selected courses AND statuses
  const visibleBatches = useMemo(() => {
    return filterOptions.batches.filter((b) => {
      const courseOk  = !selectedCourses.length  || selectedCourses.includes(b.courseId);
      const statusOk  = !selectedStatuses.length || selectedStatuses.includes(b.status);
      return courseOk && statusOk;
    });
  }, [filterOptions.batches, selectedCourses, selectedStatuses]);

  // When courses change, drop batch selections that no longer appear in visible list
  const handleCoursesChange = (newCourses) => {
    setSelectedCourses(newCourses);
    setSelectedBatches((prev) =>
      prev.filter((bId) => {
        const b = filterOptions.batches.find((x) => x.value === bId);
        if (!b) return false;
        const courseOk = !newCourses.length || newCourses.includes(b.courseId);
        const statusOk = !selectedStatuses.length || selectedStatuses.includes(b.status);
        return courseOk && statusOk;
      })
    );
  };

  // When statuses change, drop batch selections that no longer match
  const handleStatusesChange = (newStatuses) => {
    setSelectedStatuses(newStatuses);
    setSelectedBatches((prev) =>
      prev.filter((bId) => {
        const b = filterOptions.batches.find((x) => x.value === bId);
        if (!b) return false;
        const courseOk = !selectedCourses.length || selectedCourses.includes(b.courseId);
        const statusOk = !newStatuses.length || newStatuses.includes(b.status);
        return courseOk && statusOk;
      })
    );
  };

  const handleApply = useCallback(async () => {
    setLoading(true);
    setError(null);
    setApplied(true);
    try {
      const params = {};
      if (selectedCourses.length)  params.courseIds     = selectedCourses.join(',');
      if (selectedBatches.length)  params.batchIds      = selectedBatches.join(',');
      if (selectedStatuses.length) params.batchStatuses = selectedStatuses.join(',');
      const res = await axiosInstance.get('/admin/dashboard/hnd-submission-analysis', { params });
      setData(res?.data ?? null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCourses, selectedBatches, selectedStatuses]);

  const totalRepeat = useMemo(() => {
    if (!data?.courses) return 0;
    return data.courses.reduce((n, c) =>
      n + c.batches.reduce((m, b) => m + (b.totalRepeat ?? 0), 0), 0);
  }, [data]);

  const totalNotSubmitted = useMemo(() => {
    if (!data?.courses) return 0;
    return data.courses.reduce((n, c) =>
      n + c.batches.reduce((m, b) => m + (b.totalNotSubmitted ?? 0), 0), 0);
  }, [data]);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100">
            <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">HND Repeat &amp; Not Submission Analysis</h1>
            <p className="text-xs text-slate-500">
              Subjects with passed deadlines · students with Repeat marks or no submission
            </p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-52">
            <MultiSelect
              label="Course"
              options={filterOptions.courses}
              value={selectedCourses}
              onChange={handleCoursesChange}
              placeholder="All HND Courses"
            />
          </div>
          <div className="w-52">
            <MultiSelect
              label="Batch Status"
              options={BATCH_STATUS_OPTIONS}
              value={selectedStatuses}
              onChange={handleStatusesChange}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-64">
            <MultiSelect
              label="Batch"
              options={visibleBatches}
              value={selectedBatches}
              onChange={setSelectedBatches}
              placeholder="All Batches"
              disabled={visibleBatches.length === 0}
            />
          </div>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-60"
          >
            {loading ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
                </svg>
                Loading…
              </>
            ) : 'Apply'}
          </button>
          {applied && !loading && !error && data?.courses?.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams();
                if (selectedCourses.length)  params.set('courseIds',     selectedCourses.join(','));
                if (selectedBatches.length)  params.set('batchIds',      selectedBatches.join(','));
                if (selectedStatuses.length) params.set('batchStatuses', selectedStatuses.join(','));
                window.open(`/admin/report/hnd-submission-analysis/analyse?${params.toString()}`, '_blank', 'noopener,noreferrer');
              }}
              className="flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-5 py-2 text-sm font-semibold text-violet-700 shadow-sm transition hover:bg-violet-100"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
              </svg>
              Analyse
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {!applied && !loading && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <svg className="mb-3 h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6M5 21h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" />
            </svg>
            <p className="text-sm">Select filters and click <strong className="text-slate-500">Apply</strong> to generate the report.</p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <svg className="mr-2 h-5 w-5 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
            </svg>
            <span className="text-sm">Loading analysis…</span>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && applied && !error && data && (
          <>
            {/* Summary bar */}
            <div className="mb-6 flex flex-wrap gap-3">
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Courses</p>
                <p className="mt-0.5 text-2xl font-bold text-slate-800">{data.courses?.length ?? 0}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Batches</p>
                <p className="mt-0.5 text-2xl font-bold text-slate-800">
                  {data.courses?.reduce((n, c) => n + c.batches.length, 0) ?? 0}
                </p>
              </div>
              <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-red-400">Total Repeat</p>
                <p className="mt-0.5 text-2xl font-bold text-red-700">{totalRepeat}</p>
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-500">Not Submitted</p>
                <p className="mt-0.5 text-2xl font-bold text-amber-700">{totalNotSubmitted}</p>
              </div>
            </div>

            {data.courses?.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white px-6 py-12 text-center text-slate-400">
                <svg className="mx-auto mb-3 h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                </svg>
                <p className="text-sm font-medium">No issues found for the selected filters.</p>
                <p className="mt-1 text-xs">All HND students have submitted on time or no deadlines have passed yet.</p>
              </div>
            ) : (
              data.courses.map((course) => (
                <div key={course.courseId} className="mb-8">
                  {/* Course heading */}
                  <div className="mb-4 flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-200" />
                    <div className="flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-1">
                      <span className="text-sm font-bold text-teal-700">
                        {course.courseShortName || course.courseName}
                      </span>
                      {course.courseShortName && course.courseName !== course.courseShortName && (
                        <span className="text-xs text-teal-500">— {course.courseName}</span>
                      )}
                      {course.scheduleType && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize leading-none
                          ${course.scheduleType === 'weekend' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                          {course.scheduleType}
                        </span>
                      )}
                    </div>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>

                  {course.batches.map((batch) => (
                    <BatchBlock key={batch.batchId} batch={batch} />
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
